import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useBffHardening from '../api/harness/useBffHardening';
import {
  exchangeVerificationCapability,
  hasStoredSession,
  restoreVerificationSession,
} from './verificationSession';
import {
  withoutCapabilityFragment,
  withoutCapabilityQuery,
} from './returnPath';

const capabilityIn = (value: string) =>
  new URLSearchParams(value.replace(/^[?#]/, '')).get('cap');

// Links carry the capability in the fragment, which browsers never send to
// servers or analytics; older links carry it in the query string.
const readCapability = (location: { search: string; hash: string }) =>
  capabilityIn(location.hash) || capabilityIn(location.search);

const useVerificationSession = (verificationId?: string): boolean => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enabled, isResolved } = useBffHardening();
  const [capability] = React.useState(() => readCapability(location));
  const pending = React.useRef<{ id: string; done: Promise<boolean> } | null>(
    null,
  );
  const [readyFor, setReadyFor] = React.useState<string | null>(null);
  // Without a capability or a stored session there is nothing to exchange or
  // restore, so the page loads at once, as it does with the flag off.
  const hasSessionWork =
    !!capability || (!!verificationId && hasStoredSession(verificationId));
  const sessionsOn = enabled && isResolved;

  React.useEffect(() => {
    const { pathname, search, hash } = location;
    if (!sessionsOn || !readCapability({ search, hash })) return;
    navigate(
      `${pathname}${withoutCapabilityQuery(search)}${withoutCapabilityFragment(
        hash,
      )}`,
      { replace: true },
    );
  }, [sessionsOn, location.pathname, location.search, location.hash, navigate]);

  React.useEffect(() => {
    if (!verificationId || !hasSessionWork || !sessionsOn) return undefined;
    let cancelled = false;

    if (pending.current?.id !== verificationId) {
      const useCapability = capability && !pending.current;
      pending.current = {
        id: verificationId,
        done: useCapability
          ? exchangeVerificationCapability(verificationId, capability)
          : restoreVerificationSession(verificationId),
      };
    }
    pending.current.done.finally(() => {
      if (!cancelled) setReadyFor(verificationId);
    });

    return () => {
      cancelled = true;
    };
  }, [capability, verificationId, hasSessionWork, sessionsOn]);

  if (!verificationId) return false;
  if (!hasSessionWork) return true;
  if (!isResolved) return false;
  return !enabled || readyFor === verificationId;
};

export default useVerificationSession;
